import { Spinner } from "@via/components/loading/loading.tsx";
import {
  useActiveTags,
  useAssignEntityTag,
  useEntityTags,
  useRemoveEntityTag,
} from "@via/api/entity-tags.api.tsx";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Listbox } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { TaggableEntityType } from "common";
import { type JSX } from "react";

interface EntityTagsSectionProps {
  entityType: TaggableEntityType;
  entityId: string;
  canEdit: boolean;
}

export default function EntityTagsSection({
  entityType,
  entityId,
  canEdit,
}: EntityTagsSectionProps): JSX.Element {
  const { data: tagsData, isLoading: isLoadingTags } = useEntityTags(
    entityType,
    entityId
  );
  const { data: activeTagsData } = useActiveTags(entityType);
  const { mutate: assignTag, isPending: isAssigning } = useAssignEntityTag();
  const { mutate: removeTag } = useRemoveEntityTag();

  const assignedTags = tagsData?.data ?? [];
  const allActiveTags = activeTagsData?.data ?? [];

  const assignedTagIds = new Set(assignedTags.map(t => t.id));
  const availableTags = allActiveTags.filter(t => !assignedTagIds.has(t.id));

  const handleAssign = (tagId: string) => {
    if (!tagId) return;
    assignTag({ entityType, entityId, tagId });
  };

  const handleRemove = (tagId: string) => {
    removeTag({ entityType, entityId, tagId });
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <label className="block text-sm font-medium leading-6 text-gray-900">
        Tags
      </label>
      <p className="text-sm text-gray-500 mt-1">
        Assign tags to categorize this item.
      </p>

      {isLoadingTags ? (
        <div className="mt-3">
          <Spinner loading={true}>Loading tags...</Spinner>
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {assignedTags.length === 0 && (
              <span className="text-sm text-gray-400 italic">
                No tags assigned
              </span>
            )}
            {assignedTags.map(tag => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-x-1 rounded-full px-3 py-1 text-xs font-medium text-white"
                style={{ backgroundColor: tag.color ?? "#6B7280" }}>
                {tag.name}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemove(tag.id)}
                    className="ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full hover:bg-white/20">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>

          {canEdit && availableTags.length > 0 && (
            <div className="mt-3 w-64">
              <Listbox
                value=""
                onChange={(tagId: string) => handleAssign(tagId)}
                disabled={isAssigning}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6">
                    <span className="block truncate text-gray-400">
                      {isAssigning ? "Assigning..." : "Add a tag..."}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {availableTags.map(tag => (
                      <Listbox.Option
                        key={tag.id}
                        value={tag.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active
                              ? "bg-indigo-600 text-white"
                              : "text-gray-900"
                          }`
                        }>
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: tag.color ?? "#6B7280",
                            }}
                          />
                          <span className="block truncate">{tag.name}</span>
                        </div>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
            </div>
          )}
        </>
      )}
    </div>
  );
}
